import { useTranslation } from 'react-i18next'

import { CardBadge } from '#/shared/api/generated/billing/models'
import { UserCardIcon } from '#/shared/ui/icons'
import { Input } from '#/shared/ui/input'
import { Label } from '#/shared/ui/label'
import { Skeleton } from '#/shared/ui/skeleton'
import { Textarea } from '#/shared/ui/textarea'
import { formatNoteTimestamp } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'

import {
  CARD_BADGE_LABEL_KEY,
  CARD_BRAND_ICON,
  CARD_BRAND_LABEL,
} from '../model/saved-cards'
import type { SavedCard } from '../model/saved-cards'

interface CardFormProps {
  savedCards: ReadonlyArray<SavedCard>
  selectedCardId: string
  onSelectCard: (cardId: string) => void
  isLoading: boolean
  amount: string
  onAmountChange: (next: string) => void
  amountOverLimit: boolean
  chargeTotalLabel: string
  reference: string
  onReferenceChange: (next: string) => void
  onAddCard: () => void
}

function CardBrandChip({ brand }: { brand: SavedCard['brand'] }) {
  const Icon = CARD_BRAND_ICON[brand]
  return (
    <span
      aria-label={CARD_BRAND_LABEL[brand]}
      role="img"
      className="flex shrink-0 items-center"
    >
      <Icon className="h-7 w-auto" />
    </span>
  )
}

const MASK_GROUP = '∗ ∗ ∗ ∗'

function SavedCardSkeleton() {
  return (
    <div className="flex w-full items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5">
      <Skeleton className="size-4 shrink-0 rounded-full" />
      <Skeleton className="h-7 w-10 shrink-0 rounded" />
      <div className="flex min-w-0 flex-1 flex-col gap-1.25">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="h-3 w-24" />
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <Skeleton className="h-3 w-12" />
      </div>
    </div>
  )
}

function CardsEmpty() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center justify-center gap-2.5 rounded-md border border-dashed border-gray-200 bg-gray-50 px-6 py-8 text-center">
      <div className="flex size-11 items-center justify-center rounded-full bg-white ring-1 ring-gray-100 ring-inset">
        <UserCardIcon className="size-5 text-gray-300" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-gray-900">
          {t('settlePayment.card.empty')}
        </p>
        <p className="text-xs leading-relaxed text-gray-400">
          {t('settlePayment.card.emptyHint')}
        </p>
      </div>
    </div>
  )
}

function RadioIndicator({ active }: { active: boolean }) {
  if (active) {
    return (
      <span
        aria-hidden
        className="block size-4 shrink-0 rounded-full border-[5px] border-primary bg-white"
      />
    )
  }
  return (
    <span
      aria-hidden
      className="block size-4 shrink-0 rounded-full border-[1.5px] border-paper-divider-strong bg-white"
    />
  )
}

function SavedCardRow({
  card,
  active,
  onSelect,
}: {
  card: SavedCard
  active: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onSelect}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
        active
          ? 'border-primary bg-blue-100'
          : 'border-gray-200 bg-white hover:bg-gray-50',
      )}
    >
      <RadioIndicator active={active} />
      <CardBrandChip brand={card.brand} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-gray-900">
          {MASK_GROUP} {card.last4}
        </span>
        <span className="flex min-w-0 items-center gap-1.25 text-xs text-paper-text-faint">
          <span className="truncate">{card.holderName}</span>
          <span aria-hidden>·</span>
          <span
            className={cn(
              'shrink-0',
              card.badge === CardBadge.cca_on_file && 'text-green-500',
            )}
          >
            {t(CARD_BADGE_LABEL_KEY[card.badge])}
          </span>
          <span aria-hidden>·</span>
          <span className="shrink-0">
            {t('settlePayment.card.updatedAt', {
              date: formatNoteTimestamp(card.updatedAt),
            })}
          </span>
        </span>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-xs text-gray-500">
          {t('settlePayment.card.exp', { exp: card.expiry })}
        </span>
        {card.isDefault && (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-2xs-plus text-gray-500">
            {t('settlePayment.card.default')}
          </span>
        )}
      </div>
    </button>
  )
}

export function CardForm({
  savedCards,
  selectedCardId,
  onSelectCard,
  isLoading,
  amount,
  onAmountChange,
  amountOverLimit,
  chargeTotalLabel,
  reference,
  onReferenceChange,
  onAddCard,
}: CardFormProps) {
  const { t } = useTranslation()
  const showEmpty = !isLoading && savedCards.length === 0
  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-2">
        <span className="text-xs text-gray-500">
          {t('settlePayment.card.chargeWith')}
        </span>
        <div role="radiogroup" className="flex w-full flex-col gap-2">
          {isLoading && (
            <>
              <SavedCardSkeleton />
              <SavedCardSkeleton />
              <SavedCardSkeleton />
            </>
          )}
          {showEmpty && <CardsEmpty />}
          {savedCards.map((card) => (
            <SavedCardRow
              key={card.id}
              card={card}
              active={card.id === selectedCardId}
              onSelect={() => onSelectCard(card.id)}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={onAddCard}
          className="self-start cursor-pointer text-sm font-medium text-primary hover:underline"
        >
          {t('settlePayment.card.addNew')}
        </button>
      </div>
      <div className="flex flex-col gap-0.5">
        <Label size="sm">{t('settlePayment.amountToCharge')}</Label>
        <Input
          inputSize="sm"
          mask="$"
          numberFormat
          error={amountOverLimit}
          placeholder="0.00"
          className="h-10 rounded-md px-2.5"
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
        />
        {amountOverLimit && (
          <p className="text-xs text-red-500">
            {t('settlePayment.card.amountOverLimit', {
              total: chargeTotalLabel,
            })}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-0.5">
        <Label size="sm">{t('settlePayment.reference')}</Label>
        <Textarea
          textareaSize="sm"
          rows={2}
          placeholder={t('settlePayment.referencePlaceholder')}
          value={reference}
          onChange={(e) => onReferenceChange(e.target.value)}
        />
      </div>
    </div>
  )
}
